import { useState, useRef, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { BellIcon, CheckIcon } from '@heroicons/react/24/outline'
import clsx from 'clsx'
import { notificationService, getStreamUrl, type Notification } from '../../services/notifications'
import { timeAgo } from '../../theme'

const categoryStyles: Record<string, string> = {
  info: 'bg-info-100 text-info-700',
  payment: 'bg-success-100 text-success-700',
  escalation: 'bg-warning-100 text-warning-700',
  system: 'bg-stone-100 text-stone-600',
}

export default function NotificationBell() {
  const [open, setOpen] = useState(false)
  const panelRef = useRef<HTMLDivElement>(null)
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  // Poll unread count every 30 seconds
  const { data: unreadData } = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: notificationService.getUnreadCount,
    refetchInterval: 30000,
  })

  // Fetch recent notifications when panel is open
  const { data: notificationsData, isLoading } = useQuery({
    queryKey: ['notifications', 'recent'],
    queryFn: () => notificationService.list(1, 10),
    enabled: open,
  })

  // SSE connection for real-time notification updates
  useEffect(() => {
    let es: EventSource | null = null
    let retryTimeout: ReturnType<typeof setTimeout>

    const connect = async () => {
      const url = await getStreamUrl()
      if (!url) return

      es = new EventSource(url)

      es.addEventListener('notification', () => {
        queryClient.invalidateQueries({ queryKey: ['notifications'] })
      })

      es.onerror = () => {
        es?.close()
        es = null
        // Retry after 60s — polling continues as fallback
        retryTimeout = setTimeout(connect, 60_000)
      }
    }

    connect()

    return () => {
      es?.close()
      clearTimeout(retryTimeout)
    }
  }, [queryClient])

  const markReadMutation = useMutation({
    mutationFn: (id: string) => notificationService.markAsRead(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
    },
  })

  const markAllReadMutation = useMutation({
    mutationFn: () => notificationService.markAllAsRead(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] })
    },
  })

  // Close panel on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        setOpen(false)
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [open])

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.is_read) {
      markReadMutation.mutate(notification.id)
    }
    if (notification.link) {
      navigate(notification.link)
      setOpen(false)
    }
  }

  const unreadCount = unreadData?.count ?? 0
  const notifications = notificationsData?.items ?? []

  return (
    <div className="relative" ref={panelRef}>
      {/* Bell button */}
      <button
        onClick={() => setOpen(!open)}
        className="relative p-2 text-stone-500 hover:text-stone-700 rounded-lg hover:bg-stone-100 transition-colors"
        aria-label="Notifications"
      >
        <BellIcon className="h-5 w-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-clay-500 px-1 text-[10px] font-bold text-white">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown panel */}
      {open && (
        <div className="absolute right-0 mt-2 w-80 sm:w-96 bg-white rounded-xl shadow-xl border border-stone-100 overflow-hidden z-50">
          {/* Panel header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-stone-100">
            <h3 className="font-outfit text-h4 text-stone-800">Notifications</h3>
            {unreadCount > 0 && (
              <button
                onClick={() => markAllReadMutation.mutate()}
                disabled={markAllReadMutation.isPending}
                className="inline-flex items-center gap-1 text-caption text-teal-600 hover:text-teal-700 font-medium transition-colors"
              >
                <CheckIcon className="h-3.5 w-3.5" />
                Mark all as read
              </button>
            )}
          </div>

          {/* Notification list */}
          <div className="max-h-[400px] overflow-y-auto divide-y divide-stone-50">
            {isLoading ? (
              <div className="p-8 text-center">
                <div className="w-5 h-5 spinner mx-auto" />
              </div>
            ) : notifications.length === 0 ? (
              <div className="p-8 text-center">
                <BellIcon className="h-8 w-8 text-stone-300 mx-auto" />
                <p className="mt-2 font-inter text-body-sm text-stone-500">
                  No notifications yet
                </p>
              </div>
            ) : (
              notifications.map((n) => (
                <button
                  key={n.id}
                  onClick={() => handleNotificationClick(n)}
                  className={clsx(
                    'w-full px-4 py-3 text-left hover:bg-stone-50 transition-colors',
                    !n.is_read && 'bg-teal-50/40',
                  )}
                >
                  <div className="flex items-start gap-3">
                    {/* Unread indicator */}
                    <div className="pt-1.5 flex-shrink-0">
                      <div
                        className={clsx(
                          'w-2 h-2 rounded-full',
                          n.is_read ? 'bg-transparent' : 'bg-teal-500',
                        )}
                      />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p
                          className={clsx(
                            'font-inter text-body-sm truncate',
                            n.is_read ? 'text-stone-600' : 'text-stone-800 font-medium',
                          )}
                        >
                          {n.title}
                        </p>
                        <span
                          className={clsx(
                            'flex-shrink-0 px-1.5 py-0.5 rounded text-[10px] font-medium uppercase tracking-wider',
                            categoryStyles[n.category] || categoryStyles.info,
                          )}
                        >
                          {n.category}
                        </span>
                      </div>
                      {n.body && (
                        <p className="font-inter text-caption text-stone-500 mt-0.5 line-clamp-2">
                          {n.body}
                        </p>
                      )}
                      <p className="font-inter text-caption text-stone-400 mt-1">
                        {timeAgo(n.created_at)}
                      </p>
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  )
}
